/**
 * KOSULLAR (Conditions) — 5e SRD 5.1, 14 kosul.
 *
 * Buradaki model bilincli olarak SADEDIR: kosullar birbirleriyle etkilesip
 * zincirleme etki uretmez. Daha zor kosullar (Etkilemek/Zorla Tutmak gibi
 * ek eylemler) motorun degil, turun yonetiminin konusudur.
 */

import type { AppliedCondition } from './types'

interface ConditionDef extends Omit<AppliedCondition, 'slug' | 'label' | 'labelTr'> {
  label: string
  labelTr: string
}

const BASE = {
  canMove: true,
  canSpeak: true,
  canSee: true,
  canAct: true,
  canAttack: true,
  incomingAttack: 'none',
  ownAttack: 'none',
} as const

function def(label: string, labelTr: string, overrides: Partial<ConditionDef> = {}): ConditionDef {
  return { ...BASE, label, labelTr, ...overrides } as ConditionDef
}

export const CONDITION_DEFS: Record<string, ConditionDef> = {
  blinded: def('Blinded', 'Kör', {
    canSee: false,
    incomingAttack: 'advantage',
    ownAttack: 'disadvantage',
  }),
  charmed: def('Charmed', 'Büyülenmiş', {
    incomingAttack: 'advantage',
  }),
  deafened: def('Deafened', 'Sağır', { canSee: true }),
  frightened: def('Frightened', 'Korkmuş', { ownAttack: 'disadvantage' }),
  grappled: def('Grappled', 'Kavramanış', { canMove: false }),
  incapacitated: def('Incapacitated', 'Hareketsiz', {
    canAct: false,
    canSpeak: false,
  }),
  invisible: def('Invisible', 'Görünmez', { incomingAttack: 'advantage', ownAttack: 'advantage' }),
  paralyzed: def('Paralyzed', 'Felç', {
    canMove: false,
    canAct: false,
    canSpeak: false,
    incomingAttack: 'advantage',
  }),
  petrified: def('Petrified', 'Taşlaşmış', {
    canMove: false,
    canAct: false,
    canSpeak: false,
  }),
  poisoned: def('Poisoned', 'Zehirlenmiş', { incomingAttack: 'disadvantage' }),
  prone: def('Prone', 'Yere Yatık', { ownAttack: 'disadvantage' }),
  restrained: def('Restrained', 'Bağlı', {
    canMove: false,
    ownAttack: 'disadvantage',
    incomingAttack: 'advantage',
  }),
  stunned: def('Stunned', 'Sersem', {
    canMove: false,
    canAct: false,
    canSpeak: false,
  }),
  unconscious: def('Unconscious', 'Bilinçsiz', {
    canMove: false,
    canAct: false,
    canSpeak: false,
    canSee: false,
    canAttack: false,
  }),
}

/** Kullanilabilir tum kosul anahtarlari. */
export const CONDITION_KEYS = Object.keys(CONDITION_DEFS)

/**
 * Kosulu cozumler. Bilinmeyen bir kosul slug'i verilirse guvenli varsayilan
 * dondurulur ve `known: false` isaretlenir (veri temizligi icin).
 */
export function resolveCondition(slug: string): AppliedCondition & { known: boolean } {
  const normalized = slug.trim().toLowerCase().replace(/\s+/g, '_')
  const found = CONDITION_DEFS[normalized]
  if (found) {
    return { slug: normalized, known: true, ...found }
  }
  return {
    slug: normalized,
    known: false,
    label: slug,
    labelTr: slug,
    canMove: true,
    canSpeak: true,
    canSee: true,
    canAct: true,
    canAttack: true,
    incomingAttack: 'none',
    ownAttack: 'none',
  }
}

/**
 * Birden fazla kosulu birlestirir. En kotu sonuc buz olur (kapali kapi).
 * Kosullu dusulup birbirini gertileyebilir; bu fonksiyon sadece
 * yalniz (kosulsuz) kapatma kurallarini uygular.
 */
export function combineConditions(slugs: string[]): {
  conditions: AppliedCondition[]
  unknown: string[]
  aggregate: {
    canMove: boolean
    canSpeak: boolean
    canSee: boolean
    canAct: boolean
    canAttack: boolean
  }
} {
  const conditions: AppliedCondition[] = []
  const unknown: string[] = []

  for (const slug of slugs) {
    const resolved = resolveCondition(slug)
    if (!resolved.known) unknown.push(slug)
    conditions.push(resolved)
  }

  const all = (fn: (c: AppliedCondition) => boolean) => conditions.every(fn)

  return {
    conditions,
    unknown,
    aggregate: {
      canMove: all((c) => c.canMove),
      canSpeak: all((c) => c.canSpeak),
      canSee: all((c) => c.canSee),
      canAct: all((c) => c.canAct),
      canAttack: all((c) => c.canAttack),
    },
  }
}
